import { logD, logE } from "@blacknut/logging/dist";
import {
  Focusable,
  FocusableSectionProps,
  LeaveFor,
} from "@blacknut/spatialnav-sdk/dist";
import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { LOGGING_TAG } from "../../../../utils/Utils";
import HorizontalTileList from "../../../List/HorizontalTileList";
import styles from "./styles.module.scss";
import clsx from "clsx";
import { useProfiler } from "../../../../hooks/useProfiler";
import {
  EntitlementCRMInfo,
  List,
  Page,
  getImageByOrientation,
} from "@blacknut/javascript-sdk/dist";
import { useMoreLink } from "src/utils/useMoreLink";
import { ReactComponent as Arrow } from "../../../../assets/dist/ic_arrow.svg";
import { AppLayout, getEntitlementCRMPriceInfo, State } from "core/redux";
import { useLayout } from "core/utils";
import { useOrientation } from "src/utils/OrientationContext";
import { useDispatch, useSelector } from "react-redux";

interface BrandedViewProps extends Pick<FocusableSectionProps, "leaveFor" | "disabled"> {
  list: List;
  leaveFor?: LeaveFor;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  scrollable?: boolean;
  className?: string;
  page?: Page;
}

const BrandedView = ({
  list,
  leaveFor,
  onFocus,
  scrollable,
  className,
  page,
}: BrandedViewProps) => {
  const { crmInfo } = useSelector((state: State) => state.entitlementState);
  const dispatch = useDispatch();
  const layout = useLayout();
  const isPhone = layout === AppLayout.PHONE;

  logD(LOGGING_TAG, "BrandedView");
  const [brandedContainerRef, inView] = useInView({
    threshold: 0,
    rootMargin: "33% 0px",
  });

  const { orientation } = useOrientation();

  let sectionTitleColor: string | undefined;
  const { handleMoreLink } = useMoreLink(list.moreLink);
  // Get the first image
  const backgroundImage = getImageByOrientation(orientation, list.imgBackground ?? []);
  const logo = getImageByOrientation(orientation, list.imgLogo ?? []);
  if (backgroundImage) {
    if (backgroundImage.brandedOption === "brandedDark") {
      sectionTitleColor = "#191414";
    } else if (backgroundImage.brandedOption === "brandedLight") {
      sectionTitleColor = "#fff";
    }
  }
  const style = backgroundImage
    ? { backgroundImage: `url(${backgroundImage.url})` }
    : undefined;
  const Root = useProfiler("brandedList", { uuid: list.uuid });
  const hasMoreLinkIcon = list.moreLink;

  let entitlementCRMInfo: EntitlementCRMInfo | undefined;
  if (list.entitlement) {
    entitlementCRMInfo = crmInfo[list.entitlement];
  }

  useEffect(() => {
    const init = async () => {
      if (!entitlementCRMInfo && list.entitlement) {
        try {
          await getEntitlementCRMPriceInfo(list.entitlement)(dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting entilement crm info: %o", e);
        }
      }
    };
    init();
  }, [dispatch, entitlementCRMInfo, list.entitlement]);

  return (
    <Root>
      <div
        ref={brandedContainerRef}
        style={inView ? style : undefined}
        key={list.uuid}
        className={clsx(
          styles.container,
          logo && styles.hasLogo,
          !scrollable && styles.nonScrollable,
          className,
        )}
      >
        {/* aspectRatio scss mixin require only one child */}
        <div className={styles.aspectratiochild}>
          <div className={clsx([styles.titleContainer, isPhone && styles.isPhone])}>
            {inView && logo && (
              <div
                className={clsx(
                  styles.containerLogo,
                  hasMoreLinkIcon && styles.titleClickable,
                )}
              >
                <img onClick={handleMoreLink} className={styles.logo} src={logo.url} />
              </div>
            )}
            {!logo &&
              list.title &&
              (layout === AppLayout.TV ? (
                <div className={clsx(styles.title)} data-testid="brand-title">
                  {list.title}
                  {process.env.NODE_ENV === "development" && `(${list.display})`}
                </div>
              ) : (
                <Focusable
                  className={clsx(
                    styles.title,
                    hasMoreLinkIcon && styles.titleClickable,
                  )}
                  onClick={handleMoreLink}
                  onPressEnter={handleMoreLink}
                  data-testid="brand-title"
                >
                  {list.title}
                  {process.env.NODE_ENV === "development" && `(${list.display})`}
                  <Arrow />
                </Focusable>
              ))}

            {entitlementCRMInfo?.listText && (
              <div
                className={styles.priceText}
                dangerouslySetInnerHTML={{ __html: entitlementCRMInfo.listText }}
              />
            )}
          </div>

          <HorizontalTileList
            list={list}
            sectionTitleColor={sectionTitleColor}
            leaveFor={leaveFor}
            inView={inView}
            onFocus={onFocus}
            className={clsx(
              styles.brandedViewList,
              scrollable && styles.scrollable,
              list.moreLink && styles.moreLink,
            )}
            arrowClassName={styles.arrow}
            isBranded={true}
            scrollable={scrollable}
            page={page}
            showTitle={!logo}
          />
        </div>
      </div>
    </Root>
  );
};

export default BrandedView;
